/**
 * CountdownWidget — odliczanie do daty docelowej (`config.targetIso`). Bez
 * backendu; tick co sekundę przez `setInterval`. Po minięciu daty pokazuje
 * stan „minęło".
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { WidgetRenderProps } from '../types';

interface Remaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  past: boolean;
}

function computeRemaining(targetMs: number, nowMs: number): Remaining {
  const diff = targetMs - nowMs;
  const past = diff <= 0;
  const abs = Math.abs(diff);
  const sec = Math.floor(abs / 1000);
  return {
    days: Math.floor(sec / 86400),
    hours: Math.floor((sec % 86400) / 3600),
    minutes: Math.floor((sec % 3600) / 60),
    seconds: sec % 60,
    past,
  };
}

export function CountdownWidget({ widget }: WidgetRenderProps) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Hooki muszą iść przed early-return — zawężenie typu poniżej.
  const targetMs = useMemo(() => {
    if (widget.type !== 'countdown') return null;
    const iso = widget.config.targetIso;
    if (!iso) return null;
    const ms = new Date(iso).getTime();
    return Number.isNaN(ms) ? null : ms;
  }, [widget]);

  if (widget.type !== 'countdown') return null;

  if (targetMs === null) {
    return (
      <p className="flex h-full items-center justify-center text-sm text-text-tertiary">
        {t('tenant.dashboard.widgets.countdown.empty')}
      </p>
    );
  }

  const r = computeRemaining(targetMs, now);
  const units: { value: number; label: string }[] = [
    { value: r.days, label: t('tenant.dashboard.widgets.countdown.days') },
    { value: r.hours, label: t('tenant.dashboard.widgets.countdown.hours') },
    { value: r.minutes, label: t('tenant.dashboard.widgets.countdown.minutes') },
    { value: r.seconds, label: t('tenant.dashboard.widgets.countdown.seconds') },
  ];

  return (
    <div className="flex h-full flex-col items-center justify-center gap-2">
      <div className="flex items-baseline gap-3">
        {units.map((u) => (
          <div key={u.label} className="flex flex-col items-center">
            <span className="text-2xl font-bold tabular-nums text-text-primary">
              {u.value.toString().padStart(2, '0')}
            </span>
            <span className="text-[10px] uppercase tracking-wide text-text-tertiary">
              {u.label}
            </span>
          </div>
        ))}
      </div>
      {r.past && (
        <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
          {t('tenant.dashboard.widgets.countdown.past')}
        </span>
      )}
    </div>
  );
}
